import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { BASIC_ORDER_HISTORY_STATUSES } from 'common/constants/orderStatusGroups';
import DateField from 'common/forms/DateField';
import { addDays, format, startOfDay } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import {
  getPatientOrderHistory,
  listPatientOrders,
  shipOrderLater,
} from 'store/thunks/patientThunks';

const ShipLaterButton = ({ disabled, onSuccess, orderId, mpi }) => {
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpen = () => setDialogOpen(true);

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick') return;
    setDialogOpen(false);
  };

  const handleSubmit = async ({ shipDate }, form) => {
    if (!shipDate) return;
    const response = await dispatch(
      shipOrderLater({ orderId, shipByDate: format(shipDate, 'yyyy-MM-dd') })
    );
    if (!response) return;
    enqueueSnackbar('Order scheduled to ship later', { variant: 'success' });
    form.reset();
    setDialogOpen(false);
    dispatch(listPatientOrders({ mpi }));
    dispatch(
      getPatientOrderHistory({
        mpi,
        status: BASIC_ORDER_HISTORY_STATUSES,
        includeAllFacilities: true,
      })
    );
    onSuccess?.();
  };

  // `disabled` is an eligibility answer about the order's status - shipped, cancelled, void or
  // already in a shipment - and never a transient busy state, so the button is left out rather
  // than shown permanently greyed on an order that can never use it.
  if (disabled) return null;

  return (
    <>
      <LoadingButton
        size='small'
        variant='outlined'
        onClick={handleOpen}
        sx={{
          px: 1,
          fontSize: '0.75rem',
          minHeight: '30px',
          lineHeight: 'normal',
        }}
      >
        Ship Later
      </LoadingButton>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth='xs' fullWidth>
        <Form
          onSubmit={handleSubmit}
          render={({ handleSubmit: submitForm, submitting, values }) => (
            <form noValidate onSubmit={submitForm}>
              <DialogTitle>Ship Later</DialogTitle>
              <DialogContent>
                <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
                  Select a future date to ship this order.
                </Typography>
                <DateField
                  name='shipDate'
                  id='ShipLaterButton-shipDate'
                  label='Ship Date'
                  minDate={startOfDay(addDays(new Date(), 1))}
                  required
                />
              </DialogContent>
              <DialogActions>
                <Button
                  variant='outlined'
                  color='secondary'
                  onClick={handleClose}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={submitting}
                  disabled={!values.shipDate}
                  type='submit'
                >
                  Confirm
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </>
  );
};

ShipLaterButton.propTypes = {
  disabled: PropTypes.bool,
  onSuccess: PropTypes.func,
  orderId: PropTypes.string.isRequired,
  mpi: PropTypes.string.isRequired,
};

ShipLaterButton.defaultProps = {
  disabled: false,
  onSuccess: undefined,
};

export default ShipLaterButton;
